"use client";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Plus, Search, Filter, Edit, Eye, ShoppingBag, ShoppingCart, CreditCard, ArrowUp, X, Calendar, Clock } from "lucide-react";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { decodeHtmlEntities, formatDateTime } from "../utils";
import { AsyncSelect } from "../components/AsyncSelect";
import { brandService } from "../services/brands";
import { categoryService } from "../services/categories";
import { userService } from "../services/users";
import { useCart } from "../components/CartContext";
import { toast } from "sonner";
import { api } from "../services/api";

interface Product {
  id: string;
  sku: string;
  name: string;
  brand: string;
  category: string;
  tags: string[];
  price: string;
  originalPrice: string;
  image: string;
  stock: number;
  status: string;
  juros: string;
  totalAVista: string;
  totalParcelado: string;
  sacolas: number;
  sacolas_details: any[];
  reserved: boolean;
  description?: string;
  brand_id?: string;
  category_id?: string;
  subcategory_id?: string;
  fornecedor_id?: string;
  is_on_demand?: boolean;
  saled_at?: string | null;
  shippingPrice: string;
  slug: string;
  salePrice?: string;
  certificatePrice?: string;
  originalTotalAVista?: string;
  basePrice: string;
  baseSalePrice?: string;
  created_at?: string;
  updated_at?: string;
  available_at?: string;
}

interface CarrinhoCliente {
  nome: string;
  session_id: string;
  email: string;
  telefone: string;
}

interface ProdutosPageProps {
  onNavigate?: (page: string, data?: any) => void;
}

export function ProdutosPage({ onNavigate: _onNavigate }: ProdutosPageProps = {}) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0
  });
  const [sortConfig, setSortConfig] = useState({ sort_by: "available_at", sort_order: "desc" });

  const fetchProducts = async (page = 1, search = "", filters: any = {}, sort = sortConfig) => {
    try {
      setLoading(true);
      const params: any = {
        page,
        search,
        per_page: 10,
        sort_by: sort.sort_by,
        sort_order: sort.sort_order,
        ...filters
      };
      const response = await api.get(`/api/admin/products`, { params });
      const { data, meta } = response.data;

      // Adaptando o retorno do backend para o formato do frontend
      const mappedProducts: Product[] = data.map((p: any) => ({
        id: String(p.id),
        sku: p.sku,
        name: decodeHtmlEntities(p.name) || "Sem Nome",
        brand: decodeHtmlEntities(p.brand?.name) || "Sem Marca",
        category: decodeHtmlEntities(p.categories?.[0]?.name) || "Sem Categoria",
        tags: p.categories?.map((c: any) => decodeHtmlEntities(c.name)) || [],
        price: p.final_price, // Usando o preço final (com juros se aplicável)
        originalPrice: p.price,
        image: p.cover_url,
        stock: p.quantity,
        status: p.status, // Agora usando o status descritivo do backend
        // Garantir que os preços não quebrem se vierem nulos
        juros: p.juros_formatted || "R$ 0,00",
        totalAVista: p.final_avista_formatted || "R$ 0,00",
        totalParcelado: p.final_price_formatted || "R$ 0,00",
        sacolas: p.sacolas_count || 0,
        sacolas_details: p.sacolas_details || [],
        reserved: p.reserved || false,
        saled_at: p.saled_at || null,
        shippingPrice: p.shipping_price || "R$ 0,00",
        slug: p.slug,
        salePrice: p.sale_price ? p.final_price_formatted : null,
        certificatePrice: p.certificate_price_formatted,
        originalTotalAVista: p.original_avista_formatted || null,
        basePrice: p.base_price_formatted || "R$ 0,00",
        baseSalePrice: p.base_sale_price_formatted,
        created_at: p.created_at,
        updated_at: p.updated_at,
        available_at: p.available_at
      }));

      setProducts(mappedProducts);
      setPagination({
        current_page: meta.current_page,
        last_page: meta.last_page,
        total: meta.total
      });
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
      toast.error("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const toggleReservation = async (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const newReservedStatus = !product.reserved;

    try {
      await api.put(`/api/admin/products/${productId}/book`, {
        reserved: newReservedStatus
      });

      setProducts(prevProducts =>
        prevProducts.map(p =>
          p.id === productId
            ? {
              ...p,
              reserved: newReservedStatus,
              stock: newReservedStatus ? 0 : 1
            }
            : p
        )
      );

      toast.success(newReservedStatus ? "Produto reservado" : "Produto liberado");
    } catch (error) {
      console.error("Erro ao alterar reserva:", error);
      toast.error("Erro ao alterar status de reserva");
    }
  };

  const handleSetFirst = async (productId: string) => {
    try {
      await api.put(`/api/admin/products/${productId}/up`);
      toast.success("Produto colocado em primeiro");
      fetchProducts(pagination.current_page, searchTerm);
    } catch (error) {
      console.error("Erro ao colocar produto em primeiro:", error);
      toast.error("Erro ao colocar produto em primeiro");
    }
  };

  const [tooltip, setTooltip] = useState<{ visible: boolean, text: string, x: number, y: number }>({ visible: false, text: '', x: 0, y: 0 });
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  if (hoveredButton === "dummy_read") console.log(hoveredButton);

  const [popup, setPopup] = useState<{ visible: boolean, clientes: CarrinhoCliente[], product: Product | null }>({ visible: false, clientes: [], product: null });

  const emptyFilters = {
    marca: "",
    categoria: "",
    fornecedor: "",
    sku: "",
    nome: "",
    rascunho: false,
    disponivel: false,
    reservados: false,
    excluidos: false,
    emTransito: false,
    devolvidos: false,
  };

  const [activeFilters, setActiveFilters] = useState(emptyFilters);
  const [filterLabels, setFilterLabels] = useState({
    marca: "",
    categoria: "",
    fornecedor: ""
  });
  const [filterPopup, setFilterPopup] = useState<{
    visible: boolean,
    marca: string,
    categoria: string,
    fornecedor: string,
    sku: string,
    nome: string,
    rascunho: boolean,
    disponivel: boolean,
    reservados: boolean,
    excluidos: boolean,
    emTransito: boolean,
    devolvidos: boolean,
  }>({
    visible: false,
    ...emptyFilters,
  });


  const handleMouseEnter = (e: React.MouseEvent, text: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      text,
      x: rect.left + rect.width / 2,
      y: rect.top - 8
    });
    setHoveredButton(text);
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0 });
    setHoveredButton(null);
  };

  const handleSacolaClick = (product: Product) => {
    if (product.sacolas > 0) {
      setPopup({
        visible: true,
        clientes: product.sacolas_details,
        product
      });
    }
  };

  const closePopup = () => {
    setPopup({ visible: false, clientes: [], product: null });
  };

  const openFilterPopup = () => {
    setFilterPopup({
      visible: true,
      ...activeFilters,
    });
  };

  const closeFilterPopup = () => {
    setFilterPopup({ ...filterPopup, visible: false });
  };

  const buildApiFilters = (f: typeof emptyFilters) => {
    const filters: any = {};
    if (f.marca) filters.brand_id = f.marca;
    if (f.categoria) filters.category_id = f.categoria;
    if (f.sku) filters.sku = f.sku;
    if (f.nome) filters.name = f.nome;
    if (f.fornecedor) filters.user_id = f.fornecedor;

    const statuses = [];
    if (f.rascunho) statuses.push("draft");
    if (f.disponivel) statuses.push("published");
    if (f.excluidos) statuses.push("deleted");
    if (f.emTransito) statuses.push("returned");
    if (f.devolvidos) statuses.push("desistence");

    if (statuses.length > 0) {
      filters.status = statuses;
    }
    if (f.reservados) {
      filters.reserved = true;
    }
    return filters;
  };

  const applyFilters = () => {
    const { visible: _, ...filtersOnly } = filterPopup;
    setActiveFilters(filtersOnly);
    fetchProducts(1, searchTerm, buildApiFilters(filtersOnly));
    closeFilterPopup();
  };

  const getActiveFilterTags = () => {
    const tags: { key: keyof typeof emptyFilters; label: string }[] = [];
    if (activeFilters.marca) {
      tags.push({ key: 'marca', label: `Marca: ${filterLabels.marca || activeFilters.marca}` });
    }
    if (activeFilters.categoria) {
      tags.push({ key: 'categoria', label: `Categoria: ${filterLabels.categoria || activeFilters.categoria}` });
    }
    if (activeFilters.fornecedor) {
      tags.push({ key: 'fornecedor', label: `Fornecedor: ${filterLabels.fornecedor || activeFilters.fornecedor}` });
    }
    if (activeFilters.sku) tags.push({ key: 'sku', label: `SKU: ${activeFilters.sku}` });
    if (activeFilters.nome) tags.push({ key: 'nome', label: `Nome: ${activeFilters.nome}` });

    if (activeFilters.rascunho) tags.push({ key: 'rascunho', label: 'Status: Rascunho' });
    if (activeFilters.disponivel) tags.push({ key: 'disponivel', label: 'Status: Disponível' });
    if (activeFilters.reservados) tags.push({ key: 'reservados', label: 'Status: Reservado' });
    if (activeFilters.excluidos) tags.push({ key: 'excluidos', label: 'Status: Excluído' });
    if (activeFilters.emTransito) tags.push({ key: 'emTransito', label: 'Status: Em Trânsito' });
    if (activeFilters.devolvidos) tags.push({ key: 'devolvidos', label: 'Status: Devolvido' });

    return tags;
  };

  const clearSingleFilter = (key: keyof typeof emptyFilters) => {
    const newFilters = { ...activeFilters, [key]: emptyFilters[key] };
    setActiveFilters(newFilters);
    // Sync with filterPopup to keep modal updated if user opens it
    setFilterPopup(prev => ({ ...prev, [key]: emptyFilters[key] }));
    fetchProducts(1, searchTerm, buildApiFilters(newFilters));
  };

  const clearAllFilters = () => {
    setActiveFilters(emptyFilters);
    setFilterLabels({ marca: "", categoria: "", fornecedor: "" });
    setFilterPopup({ visible: false, ...emptyFilters });
    fetchProducts(1, searchTerm, buildApiFilters(emptyFilters));
  };

  const handleAddToCart = (product: Product, paymentType: 'avista' | 'parcelado') => {
    // Se o estoque for zero, verificamos se é uma reserva permitida ou se já foi vendido
    if (product.stock <= 0) {
      if (product.saled_at) {
        toast.error("Este produto já foi vendido e não possui mais estoque.");
        return;
      }

      if (!product.reserved) {
        toast.error("Produto sem estoque disponível");
        return;
      }
    }

    const totalPrice = paymentType === 'avista' ? product.totalAVista : product.totalParcelado;

    addItem({
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      totalPrice: totalPrice,
      image: product.image,
      paymentType: paymentType,
      shippingPrice: product.shippingPrice,
      stock: product.stock,
      reserved: product.reserved,
    });

    toast.success(`${product.name} adicionado ao carrinho (${paymentType === 'avista' ? 'À vista' : 'Parcelado'})`, {
      duration: 2000,
    });
  };

  return (
    <main className="flex-1 p-6 bg-gray-50">
      {/* Cabeçalho da Página */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#68686A]">Produtos</h1>
        <p className="text-[#68686A]">Organize e gerencie seus produtos</p>
      </div>

      {/* Filtros/Ações */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Buscar produtos..."
                className="pl-10 w-full sm:w-64"
                value={searchTerm}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchTerm(val);
                  fetchProducts(1, val, buildApiFilters(activeFilters));
                }}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <select
              className="flex-1 sm:flex-none h-10 px-3 rounded-md border border-gray-300 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
              value={`${sortConfig.sort_by}|${sortConfig.sort_order}`}
              onChange={(e) => {
                const [sort_by, sort_order] = e.target.value.split('|');
                const newSort = { sort_by, sort_order };
                setSortConfig(newSort);
                fetchProducts(1, searchTerm, buildApiFilters(activeFilters), newSort);
              }}
            >
              <option value="available_at|desc">Ordenar por Disponibilizado em</option>
              <option value="updated_at|desc">Ordenar por Atualizado em</option>
            </select>
            <Button
              variant="outline"
              onClick={openFilterPopup}
              className="flex-1 sm:flex-none border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros
            </Button>
            <Button
              onClick={() => navigate("/novo-produto")}
              className="flex-1 sm:flex-none bg-[#D4C070] hover:bg-[#849D70] text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Produto
            </Button>
          </div>
        </div>

        {/* Tags de Filtros Ativos */}
        {getActiveFilterTags().length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500 mr-1">Filtros ativos:</span>
            {getActiveFilterTags().map(tag => (
              <Badge key={tag.key} variant="secondary" className="bg-[#D4C070]/20 text-gray-800 border-none flex items-center gap-1 font-normal py-1">
                {tag.label}
                <button
                  type="button"
                  onClick={() => clearSingleFilter(tag.key)}
                  className="ml-1 text-gray-500 hover:text-red-500 transition-colors focus:outline-none flex items-center justify-center"
                >
                  <X className="h-3 w-3 cursor-pointer" />
                </button>
              </Badge>
            ))}
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-gray-500 h-6 px-2 hover:bg-transparent hover:underline"
              onClick={clearAllFilters}
            >
              Limpar todos
            </Button>
          </div>
        )}
      </div>

      {/* Lista de Produtos */}
      {loading ? (
        <main className="flex-1 p-6 bg-gray-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </main>
      ) : (
        <Card>
          {products.length > 0 ? (<CardContent className="p-0">
            <div className="divide-y">
              {products.map((product) => (
                <div key={product.id} className="flex flex-wrap md:flex-nowrap md:items-center p-4 md:p-6 hover:bg-gray-50 gap-4 md:gap-0">
                  {/* Edit Button */}
                  <div className="hidden md:flex mr-4 md:mr-6 items-center justify-center">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 relative"
                      onMouseEnter={(e) => handleMouseEnter(e, "Editar")}
                      onMouseLeave={handleMouseLeave}
                      onClick={() => navigate(`/editar-produto/${product.id}`)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Thumbnail */}
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-gray-200 rounded-md mr-4 md:mr-6 flex-shrink-0 flex items-center justify-center relative">
                    <ImageWithFallback
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover rounded-md"
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      className="md:hidden absolute -top-1.5 -right-1.5 h-6 w-6 p-0 rounded-full bg-white/95 border border-gray-200 shadow-md text-gray-700 hover:bg-gray-100 flex items-center justify-center z-10"
                      onClick={() => navigate(`/editar-produto/${product.id}`)}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Nome + Código e Subdescrição */}
                  <div className="flex-1 min-w-0 mr-4 md:mr-6">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-medium text-gray-900 text-sm md:text-lg break-words">{decodeHtmlEntities(product.name)}</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-2 break-all">SKU: {product.sku}</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className="text-xs text-gray-500 mr-1">Categorias:</span>
                      {product.tags.map((tag, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="text-xs text-gray-600 px-1 py-0 bg-gray-200 border-gray-200 whitespace-normal break-words"
                        >
                          {decodeHtmlEntities(tag)}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className="text-xs text-gray-500 mr-1">Marca:</span>
                      <Badge
                        variant="outline"
                        className="text-xs text-gray-600 px-1 py-0 bg-gray-200 border-gray-200 whitespace-normal break-words"
                      >
                        {decodeHtmlEntities(product.brand)}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">Estoque: {product.stock}</p>
                    <div className="flex flex-col gap-1.5 mt-2">
                      <div className="flex flex-wrap items-center gap-1.5 text-[11px] bg-gray-50/80 border border-gray-100 px-2 py-1 rounded w-full sm:w-max">
                        <Calendar className="h-3 w-3 text-[#D4C070]" />
                        <span className="text-gray-500 font-medium">Disponibilizado em:</span>
                        <span className="text-gray-700 font-semibold">{formatDateTime(product.available_at)}</span>
                      </div>
                      {product.updated_at && product.created_at && new Date(product.updated_at) > new Date(product.created_at) && (
                        <div className="flex flex-wrap items-center gap-1.5 text-[11px] bg-gray-50/80 border border-gray-100 px-2 py-1 rounded w-full sm:w-max">
                          <Clock className="h-3 w-3 text-gray-400" />
                          <span className="text-gray-500 font-medium">Atualizado em:</span>
                          <span className="text-gray-700 font-semibold">{formatDateTime(product.updated_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ações + Tags */}
                  <div className="w-full md:flex-1 md:mr-6 mt-2 md:mt-0 pt-2 md:pt-0 border-t md:border-t-0 border-gray-100">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm text-gray-500 mr-2">Ações:</span>
                      <div className="relative">
                        <Button
                          size="sm"
                          variant="ghost"
                          className={`h-8 w-8 p-0 relative transition-all ${product.sacolas > 0 ? 'text-[#D4C070] hover:text-[#BFAE5E] bg-[#D4C070]/5' : 'text-gray-400 opacity-40 cursor-not-allowed'}`}
                          onMouseEnter={(e) => handleMouseEnter(e, "Ver usuários com este produto no carrinho")}
                          onMouseLeave={handleMouseLeave}
                          onClick={() => product.sacolas > 0 && handleSacolaClick(product)}
                        >
                          <ShoppingBag className="h-4 w-4" />
                          {product.sacolas > 0 && (
                            <Badge className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 flex items-center justify-center text-[10px] bg-red-500 text-white border-none shadow-sm rounded-full leading-none">
                              {product.sacolas}
                            </Badge>
                          )}
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className={`h-8 w-8 p-0 relative ${(product.stock <= 0 && product.saled_at) ? 'opacity-20 cursor-not-allowed' : ''}`}
                        onMouseEnter={(e) => handleMouseEnter(e, (product.stock <= 0 && product.saled_at) ? "Produto Vendido" : "Adicionar ao carrinho (À vista)")}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => !(product.stock <= 0 && product.saled_at) && handleAddToCart(product, 'avista')}
                      >
                        <ShoppingCart className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className={`h-8 w-8 p-0 relative ${(product.stock <= 0 && product.saled_at) ? 'opacity-20 cursor-not-allowed' : ''}`}
                        onMouseEnter={(e) => handleMouseEnter(e, (product.stock <= 0 && product.saled_at) ? "Produto Vendido" : "Adicionar ao carrinho (Parcelado)")}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => !(product.stock <= 0 && product.saled_at) && handleAddToCart(product, 'parcelado')}
                      >
                        <CreditCard className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 relative"
                        onMouseEnter={(e) => handleMouseEnter(e, "Subir o produto para a primeira posição")}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => handleSetFirst(product.id)}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 relative text-[#D4C070]"
                        onMouseEnter={(e) => handleMouseEnter(e, "Ver no site (Loja)")}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => window.open(`${import.meta.env.VITE_STORE_URL}/produtos/${product.slug}`, '_blank')}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm text-gray-500 mr-2">Reservar:</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-10 w-14 p-0 relative"
                        onMouseEnter={(e) => {
                          let msg = "Reservar o produto";
                          if (product.saled_at) msg = "Produto já vendido";
                          else if (product.reserved) msg = "Liberar reserva";
                          else if (product.stock > 1) msg = "Reserva indisponível para produtos com estoque > 1";
                          else if (product.stock === 0) msg = "Sem estoque disponível para reserva";
                          handleMouseEnter(e, msg);
                        }}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => {
                          if (product.saled_at) {
                            toast.error("Produto vendido não pode ter a reserva alterada");
                            return;
                          }
                          if (product.reserved) {
                            toggleReservation(product.id);
                          } else if (product.stock === 1) {
                            toggleReservation(product.id);
                          } else if (product.stock > 1) {
                            toast.error("Apenas produtos com estoque unitário podem ser reservados");
                          } else {
                            toast.error("Sem estoque disponível");
                          }
                        }}
                      >
                        {!(product.reserved || product.saled_at) ? (
                          <div className={`flex items-center justify-center w-full h-full ${(product.stock === 0) ? 'opacity-40 grayscale' : ''}`}>
                            <div className="w-8 h-5 bg-gray-300 rounded-full relative">
                              <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200"></div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center w-full h-full">
                            <div className="w-8 h-5 bg-[#849D70] rounded-full relative">
                              <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200"></div>
                            </div>
                          </div>
                        )}
                      </Button>
                    </div>
                    <div>
                      <Badge
                        variant="default"
                        className={
                          product.saled_at
                            ? "bg-green-100 text-green-800 border-green-200"
                            : product.reserved
                              ? "bg-amber-100 text-amber-800 border-amber-200"
                              : product.status === "published"
                                ? "bg-blue-100 text-blue-800 border-blue-200"
                                : product.status === "draft"
                                  ? "bg-gray-100 text-gray-800 border-gray-200"
                                  : product.status === "returned"
                                    ? "bg-purple-100 text-purple-800 border-purple-200"
                                    : product.status === "desistence"
                                      ? "bg-rose-100 text-rose-800 border-rose-200"
                                      : "bg-red-100 text-red-800 border-red-200"
                        }
                      >
                        {product.saled_at ? "Vendido" :
                          product.reserved ? "Reservado" :
                            product.status === "published" ? "Publicado" :
                              product.status === "draft" ? "Rascunho" :
                                product.status === "deleted" ? "Deletado" :
                                  product.status === "returned" ? "Em Trânsito" :
                                    product.status === "desistence" ? "Devolvido" : product.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Coluna direita: Valores e Status */}
                  <div className="w-full md:w-auto text-left md:text-right min-w-0 flex-shrink-0 mt-3 md:mt-0 pt-3 md:pt-0 border-t md:border-t-0 border-gray-100 flex flex-col md:items-end justify-center">
                    <div className="flex flex-col text-xs text-gray-500 gap-1 mb-2 bg-white md:bg-gray-50/50 p-2.5 rounded md:border border-gray-100 w-full md:w-auto text-left min-w-[180px]">
                      {product.baseSalePrice ? (
                        <>
                          <p className="line-through decoration-red-400">Original: {product.basePrice}</p>
                          <p className="font-medium text-gray-900">Promoção: {product.baseSalePrice}</p>
                        </>
                      ) : (
                        <p className="font-medium text-gray-700">Preço: {product.basePrice}</p>
                      )}
                      {product.certificatePrice && (
                        <p>Certificado: {product.certificatePrice}</p>
                      )}
                      <p>Juros: {product.juros}</p>
                      <p>Frete: {product.shippingPrice} (não incluso no total)</p>
                    </div>

                    <div className="space-y-0.5 text-left md:text-right w-full bg-[#849D70]/5 md:bg-transparent p-2.5 md:p-0 rounded">
                      <p className="font-medium text-[#849D70] text-xs sm:text-sm">
                        Total À Vista: {product.totalAVista}
                      </p>
                      <p className="font-semibold text-gray-900 text-xs sm:text-sm">
                        Total Parcelado: {product.totalParcelado}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>) : (
            <CardContent className="p-6">
              <p className="text-center text-gray-500">Nenhum produto encontrado</p>
            </CardContent>
          )}
        </Card>
      )}

      {/* Paginação */}
      <div className="flex items-center justify-center mt-6">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.current_page === 1}
            onClick={() => fetchProducts(pagination.current_page - 1, searchTerm, buildApiFilters(activeFilters))}
          >
            {'<'}
          </Button>
          {(() => {
            const current = pagination.current_page;
            const last = pagination.last_page;
            const pages: (number | string)[] = [];
            const delta = 2;

            for (let i = 1; i <= last; i++) {
              if (i === 1 || i === last || (i >= current - delta && i <= current + delta)) {
                pages.push(i);
              } else if (pages[pages.length - 1] !== "...") {
                pages.push("...");
              }
            }

            return pages.map((page, index) => (
              page === "..." ? (
                <span key={`dots-${index}`} className="text-gray-400 px-1">...</span>
              ) : (
                <Button
                  key={page}
                  variant={pagination.current_page === page ? "default" : "outline"}
                  size="sm"
                  className={pagination.current_page === page ? "bg-[#D4C070] hover:bg-[#D4C070] text-white" : "text-[#68686A]"}
                  onClick={() => fetchProducts(page as number, searchTerm, buildApiFilters(activeFilters))}
                >
                  {page}
                </Button>
              )
            ));
          })()}
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.current_page === pagination.last_page}
            onClick={() => fetchProducts(pagination.current_page + 1, searchTerm, buildApiFilters(activeFilters))}
          >
            {'>'}
          </Button>
        </div>
      </div>

      {/* Tooltip customizado */}
      {tooltip.visible && (
        <div
          className="fixed z-[100] bg-gray-900 text-white text-[10px] font-medium px-2 py-1.5 rounded shadow-xl pointer-events-none transition-all duration-200"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
            maxWidth: '180px',
            textAlign: 'center',
            lineHeight: '1.2'
          }}
        >
          {tooltip.text}
          {/* Seta do tooltip */}
          <div
            className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"
            style={{ marginBottom: '-1px' }}
          />
        </div>
      )}

      {/* Popup de clientes no carrinho */}
      {popup.visible && (
        <div className="fixed inset-0 flex items-center justify-center z-[60] bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg relative border border-gray-100 shadow-2xl flex flex-col max-h-[85vh]">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 z-10"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="text-xl font-bold mb-1 text-gray-900 pr-8">
              Carrinhos Ativos
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Produto: <span className="font-medium text-[#D4C070]">{popup.product?.name}</span>
            </p>

            <div className="space-y-3 overflow-y-auto pr-2 custom-scrollbar flex-1">
              {popup.clientes && popup.clientes.length > 0 ? (
                popup.clientes.map((cliente, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:border-[#D4C070]/30 transition-all hover:shadow-sm">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#D4C070]/10 flex items-center justify-center text-[#D4C070] font-bold text-sm">
                          {cliente.nome?.trim() ? cliente.nome.trim().charAt(0).toUpperCase() : 'V'}
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{cliente.nome?.trim() || 'Visitante'}</p>
                          <p className="text-[10px] text-gray-400 font-mono">ID: {cliente.session_id}</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="font-semibold w-14 text-xs uppercase text-gray-400">Email</span>
                        <span className="truncate">{cliente.email || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="font-semibold w-14 text-xs uppercase text-gray-400">Fone</span>
                        <span>{cliente.telefone || '-'}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-12 text-center text-gray-400 flex flex-col items-center gap-2">
                  <ShoppingBag className="h-8 w-8 opacity-20" />
                  <p>Nenhum detalhe adicional encontrado</p>
                </div>
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end">
              <Button
                onClick={closePopup}
                className="bg-[#D4C070] hover:bg-[#BFAE5E] text-white px-10 rounded-lg font-bold"
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Popup de filtros */}
      {filterPopup.visible && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md relative border border-gray-200 shadow-xl max-h-[95vh] overflow-y-auto">
            <button
              onClick={closeFilterPopup}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="text-lg font-semibold mb-4 text-gray-900">Filtrar Produtos</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                <AsyncSelect
                  value={filterPopup.marca}
                  onChange={(id, lbl) => {
                    setFilterPopup({ ...filterPopup, marca: id || "" });
                    if (id) setFilterLabels(prev => ({ ...prev, marca: lbl }));
                  }}
                  fetcher={async (params) => {
                    const res = await brandService.getAll({ ...params, per_page: 50 });
                    return res.data;
                  }}
                  initialDisplay={filterPopup.marca ? filterLabels.marca : ""}
                  placeholder="Selecione uma marca"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categorias</label>
                <AsyncSelect
                  value={filterPopup.categoria}
                  onChange={(id, lbl) => {
                    setFilterPopup({ ...filterPopup, categoria: id || "" });
                    if (id) setFilterLabels(prev => ({ ...prev, categoria: lbl }));
                  }}
                  fetcher={async (params) => {
                    const res = await categoryService.getAll(params);
                    const items = res.data?.data || res.data || [];
                    return items.reduce((acc: any[], item: any) => {
                      if (item.parent_id > 0 && item.parent) {
                        item.name = `${decodeHtmlEntities(item.parent.name)} > ${decodeHtmlEntities(item.name)}`;
                      } else {
                        item.name = decodeHtmlEntities(item.name);
                      }
                      acc.push(item);
                      return acc;
                    }, []);
                  }}
                  initialDisplay={filterPopup.categoria ? filterLabels.categoria : ""}
                  placeholder="Selecione uma categoria"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
                <AsyncSelect
                  value={filterPopup.fornecedor}
                  onChange={(id, lbl) => {
                    setFilterPopup({ ...filterPopup, fornecedor: id || "" });
                    if (id) setFilterLabels(prev => ({ ...prev, fornecedor: lbl }));
                  }}
                  fetcher={async (params) => {
                    if (!params.search || params.search.trim().length < 2) return [];
                    const res = await userService.getAll({ ...params, has_code: 1, sort: 'first_name', order: 'asc' });
                    return res.data;
                  }}
                  initialDisplay={filterPopup.fornecedor ? filterLabels.fornecedor : ""}
                  placeholder="Selecione um fornecedor"
                  getOptionLabel={(u) => {
                    const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || "";
                    return `[${u.code || u.id}] ${fullName}${u.email ? ` (${u.email})` : ""}`;
                  }}
                  renderOption={(u) => (
                    <div className="flex items-center gap-3 py-1.5 w-full">
                      <div className="flex items-center justify-center min-w-[55px] bg-gray-800 text-white text-[10px] font-bold px-2 py-1 rounded border border-gray-700 uppercase tracking-tighter shadow-sm">
                        {u.code || u.id}
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="font-bold text-gray-900 truncate">
                          {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.name}
                        </span>
                        {u.email && <span className="text-[10px] text-gray-500 truncate font-medium">{u.email}</span>}
                      </div>
                    </div>
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                <input
                  type="text"
                  value={filterPopup.sku}
                  onChange={(e) => setFilterPopup({ ...filterPopup, sku: e.target.value })}
                  placeholder="SKU"
                  className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={filterPopup.nome}
                  onChange={(e) => setFilterPopup({ ...filterPopup, nome: e.target.value })}
                  placeholder="Nome do produto"
                  className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <p className="block text-sm font-medium text-gray-700 mb-1">Status</p>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="rascunho"
                    checked={filterPopup.rascunho}
                    onChange={(e) => setFilterPopup({ ...filterPopup, rascunho: e.target.checked })}
                    className="h-4 w-4 text-[#D4C070] focus:ring-2 focus:ring-[#D4C070] border-gray-300 rounded"
                  />
                  <label htmlFor="rascunho" className="ml-2 text-sm text-gray-700">Rascunho</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="disponivel"
                    checked={filterPopup.disponivel}
                    onChange={(e) => setFilterPopup({ ...filterPopup, disponivel: e.target.checked })}
                    className="h-4 w-4 text-[#D4C070] focus:ring-2 focus:ring-[#D4C070] border-gray-300 rounded"
                  />
                  <label htmlFor="disponivel" className="ml-2 text-sm text-gray-700">Disponível</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="reservados"
                    checked={filterPopup.reservados}
                    onChange={(e) => setFilterPopup({ ...filterPopup, reservados: e.target.checked })}
                    className="h-4 w-4 text-[#D4C070] focus:ring-2 focus:ring-[#D4C070] border-gray-300 rounded"
                  />
                  <label htmlFor="reservados" className="ml-2 text-sm text-gray-700">Reservados</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="excluidos"
                    checked={filterPopup.excluidos}
                    onChange={(e) => setFilterPopup({ ...filterPopup, excluidos: e.target.checked })}
                    className="h-4 w-4 text-[#D4C070] focus:ring-2 focus:ring-[#D4C070] border-gray-300 rounded"
                  />
                  <label htmlFor="excluidos" className="ml-2 text-sm text-gray-700">Excluídos</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="emTransito"
                    checked={filterPopup.emTransito}
                    onChange={(e) => setFilterPopup({ ...filterPopup, emTransito: e.target.checked })}
                    className="h-4 w-4 text-[#D4C070] focus:ring-2 focus:ring-[#D4C070] border-gray-300 rounded"
                  />
                  <label htmlFor="emTransito" className="ml-2 text-sm text-gray-700">Em Trânsito</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="devolvidos"
                    checked={filterPopup.devolvidos}
                    onChange={(e) => setFilterPopup({ ...filterPopup, devolvidos: e.target.checked })}
                    className="h-4 w-4 text-[#D4C070] focus:ring-2 focus:ring-[#D4C070] border-gray-300 rounded"
                  />
                  <label htmlFor="devolvidos" className="ml-2 text-sm text-gray-700">Devolvidos</label>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={closeFilterPopup}
                className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
              >
                Cancelar
              </Button>
              <Button
                onClick={applyFilters}
                className="bg-[#D4C070] hover:bg-[#849D70] text-white"
              >
                Aplicar Filtros
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default ProdutosPage;
